/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import type { GetProductAvailabilitiesRequest, ProductDetails } from "@farmlands/farmlandspro-api";

import { farmlandsProApi } from "../../../services/farmlandspro-api";
import { log } from "../../../util/log";
import {
  ActivePrice,
  Product,
  ProductDetailsApiResponse,
  ProductDetailsPriceBreaks,
  StockAvailabilityType,
} from "../storage/types";

import { toActivePrices, toStockAvailabilities, transformProducts } from "./productMappers";

export { toActivePrices, toStockAvailabilities } from "./productMappers";

/** Product details API returns price breaks that are not yet on the generated `ProductDetails` type. */
type ProductDetailsWithPriceBreaks = ProductDetails & {
  priceBreaks?: ProductDetailsPriceBreaks[];
};

export const fetchProduct = async (
  id: string,
  accountNumber: string,
  branchId: string
): Promise<ProductDetailsApiResponse> => {
  const response = await farmlandsProApi.products.getProduct({
    id,
    accountNumber,
    branchId,
  });

  return response.data as ProductDetailsWithPriceBreaks as ProductDetailsApiResponse;
};

export const getStockAvailabilities = async (
  getProductAvailabilitiesRequest: GetProductAvailabilitiesRequest
): Promise<StockAvailabilityType[] | undefined> => {
  try {
    const response = await farmlandsProApi.products.getProductAvailabilities({
      getProductAvailabilitiesRequest,
    });

    if (response.status.toString().startsWith("2")) {
      return toStockAvailabilities(response.data.availabilities);
    }
    return undefined;
  } catch (error) {
    log(error);
  }
};

export const getActivePrices = async (
  accountNumber: string | undefined,
  productIds: number[],
  branchId: string | undefined
) => {
  try {
    return await farmlandsProApi.pricing.getActivePrices({
      getActivePricesRequest: {
        accountNumber,
        productIds,
        branchId,
      },
    });
  } catch (error) {
    log(error);
  }
};

const updateAltPrices = (
  products: Product[],
  productsHaveAlt: Product[],
  altProductIds: number[],
  prices: ActivePrice[]
) => {
  const altProductPrices = prices.filter((price) => altProductIds.includes(price.productId));

  const altProductsWithActivePrices = productsHaveAlt
    .map((product) => {
      const altProductPrice = altProductPrices.find(
        (ap) => ap.productId === product.alternativeProduct?.productId
      );
      if (altProductPrice) {
        return {
          ...product,
          alternativeProduct: {
            ...product.alternativeProduct,
            ...altProductPrice,
          },
        };
      }
    })
    .filter((alt) => alt !== undefined && alt.basePriceExcGst > 0);

  if (altProductsWithActivePrices.length > 0) {
    const productsWithUpdatedAltProducts = products.map((product) => {
      if (product.alternativeProduct) {
        const targetProduct = altProductsWithActivePrices.find(
          (x) => x?.productId === product.productId
        );
        return targetProduct;
      }
      return product;
    }) as Product[];

    return productsWithUpdatedAltProducts;
  }

  return products;
};

export const fetchPastProducts = async (accountNumber: string): Promise<Product[]> => {
  const response = await farmlandsProApi.products.getPastProductsV2({ accountNumber });

  return transformProducts(response.data);
};

export const getActivePricesForPastProducts = async (
  accountNumber: string | undefined,
  products: Product[],
  branchId: string | undefined
) => {
  try {
    const productIds = products.map((p) => p.productId);
    const productsHaveAlt = products.filter((p) => p.alternativeProduct);
    const altProductIds = productsHaveAlt
      .map((p) => p.alternativeProduct?.productId)
      .filter((id): id is number => id !== undefined);

    const activePricesResponse = await getActivePrices(accountNumber, productIds, branchId);

    const prices = toActivePrices(activePricesResponse?.data?.prices);
    if (!prices || prices.length === 0) {
      return;
    }

    let productsWithUpdatedAltProducts = products;

    if (altProductIds.length > 0) {
      const activePricesAltResponse = await getActivePrices(accountNumber, altProductIds, branchId);
      const altPrices = toActivePrices(activePricesAltResponse?.data?.prices).filter(
        (price) => price.basePriceExcGst > 0
      );

      if (altPrices) {
        productsWithUpdatedAltProducts = updateAltPrices(
          products,
          productsHaveAlt,
          altProductIds,
          altPrices
        ).filter((product) => product !== undefined);
      }
    }

    return productsWithUpdatedAltProducts.map((product) => {
      const activePrice = prices.find((ap) => ap.productId === product.productId);
      return activePrice ? { ...product, ...activePrice } : product;
    });
  } catch (error) {
    log(error);
  }
};
